/**
 * DataTable Component
 */

import React, { FC, useState } from 'react';
import { withTheme } from 'styled-components';
import { DataTableProps, SortOrderTypes } from './dataTable.types';
import Table from '../Table/table';
import DataTableStore from './utils/dataTableStore';
import Handler from '../../commonTypes/handler.types';
import { TheadType, HeadProps, GroupHeadProps, SortColumn } from '../Table/table.types';

// Process the column data being passed. Update values where needed and store in
// a flat array.
const processCols: Function = (thead: TheadType[], tableSortState: SortOrderTypes) => {
  const columnsState = {};
  let allColumns: HeadProps[] = [];

  thead.forEach((column: TheadType, i) => {
      const hasColGroup = (column as GroupHeadProps).groupColumns;
      if (hasColGroup) {
          const columns = (column as GroupHeadProps).groupColumns;
          allColumns = allColumns.concat(columns);
      } else {
          allColumns.push((column as HeadProps));
      }
  });

  let initialSortColumnIndex: number | undefined;
  allColumns.forEach((column, i) => {
    if (column.initialSort) initialSortColumnIndex = i;

    const {
      sortable,
      initialSort,
      sortState = SortOrderTypes.DESC,
      type = 'number',
      sortColumns = [],
      dataField,
      sortField
    } = column;

    columnsState[`col-${i}`] = {
      sortable: (typeof sortable !==  'undefined') ? sortable : true,
      sortState: initialSort && tableSortState ? tableSortState : sortState,
      type,
      sortColumns: sortColumns,
      dataField: dataField,
      sortField: sortField
    };
  });

  return {
    initialColumnsState: columnsState,
    initialSortColumnIndex
  };
};

const DataTable: FC<DataTableProps> = ({
  thead,
  tbody,
  sortState,
  highlightSortedColumn = true,
  handlers = {},
  ...otherProps
}: DataTableProps) => {

  const {
    initialColumnsState,
    initialSortColumnIndex
  } = processCols(thead, sortState);

  // Keep track of the sort states of each of the columns
  const [columnsState, setColumnsState] = useState(initialColumnsState);

  // Keep track of the currently sorted column
  const [sortColumnIndex, setSortColumnIndex] = useState(initialSortColumnIndex);

  // Set up store that holds onto and sorts the table rows when necessary
  const store = new DataTableStore(tbody ? tbody.slice() : [], columnsState, initialSortColumnIndex);

  // Retrieve the new set of table rows.
  const getTableRows  = () => {
    const sortColumn = columnsState[`col-${sortColumnIndex}`];
    return store.get(tbody, sortColumnIndex, sortColumn);
  }

  // If the header is clicked (when sortable), determine the column and update column sort states
  const handleHeaderClick = (evt: Event) => {
    const retrievedValue: string | null = (evt?.currentTarget as HTMLTableHeaderCellElement)?.getAttribute('data-col');
    let columnIndex: number = parseInt(retrievedValue || '', 10);
    columnIndex = isNaN(columnIndex) ? sortColumnIndex : columnIndex;

    const sortColumn = columnsState[`col-${columnIndex}`];
    let toggledSortColumns = [];
    let sortValue = sortColumn.sortState;
    if (sortColumnIndex === columnIndex) {
        sortValue = sortColumn.sortState === SortOrderTypes.ASC ? SortOrderTypes.DESC : SortOrderTypes.ASC;
        if (sortColumn.sortColumns.length > 0) {
            toggledSortColumns = sortColumn.sortColumns.map((column: SortColumn) => {
                const { sortOrder } = column;
                if (sortOrder !== 'toggle') {
                    column.sortOrder = sortOrder === SortOrderTypes.ASC ? SortOrderTypes.DESC : SortOrderTypes.ASC;
                }
                return column;
            });
        }

        const nextSortColumnState = { ...sortColumn, sortState: sortValue, sortColumns: toggledSortColumns };
        setColumnsState({ ...columnsState, [`col-${columnIndex}`]: nextSortColumnState });
    } else {
        setSortColumnIndex(columnIndex);
    }
    // invoking handler for column change if parent needs access
    if (handlers && handlers.onHeaderClick) {
        handlers.onHeaderClick(columnIndex, sortValue, sortColumn);
    }
  }

  // Handlers of the headers. Triggers the same even with both ENTER keypress and click
  const headerHandlers: Handler = {
    onClick: (evt: Event) => handleHeaderClick(evt),
    onKeyPress: (evt: KeyboardEvent) => evt.key === 'Enter' && handleHeaderClick(evt)
  }

  return (
    <Table
      {...otherProps}
      thead={ thead }
      tbody={ getTableRows() }
      headerHandlers={ headerHandlers }
      columnsState={ columnsState }
      columnSortIndex={ sortColumnIndex }
      hoverRowHighlight
      highlightSortedColumn={ highlightSortedColumn }
    />
  );
};

export default withTheme(DataTable);
